import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import api from "../../lib/api";
import { StatusMark } from "../../components/StatusMark";
import { GroupPill } from "../../components/GroupPill";
import { ExportMenu } from "../../components/ExportMenu";
import { formatApiErrorDetail, formatPT, groupLabel } from "../../lib/format";

const TABS = ["pending", "approved", "declined", "waitlisted"];

export default function ReefRegistrations() {
  const [tab, setTab] = useState("pending");
  const [regs, setRegs] = useState([]);
  const [classes, setClasses] = useState([]);
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const [groupBy, setGroupBy] = useState(false);

  const load = useCallback(async () => {
    const [r, c] = await Promise.all([
      api.get(`/registrations${tab ? `?status=${tab}` : ""}`),
      api.get("/classes"),
    ]);
    setRegs(r.data);
    setClasses(c.data);
  }, [tab]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const classById = useMemo(() => Object.fromEntries(classes.map((c) => [c.id, c])), [classes]);

  const filtered = useMemo(() => {
    let out = regs.filter((r) => (filter === "all" ? true : r.class_id === filter));
    out = [...out];
    if (sort === "oldest") out.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    else out.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return out;
  }, [regs, filter, sort]);

  const act = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/registrations/${id}/${action}`);
      toast.success(action === "approve" ? "Approved — added to the roster." : "Declined.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const printCols = [
    { key: "student_name", label: "Name" },
    { key: "student_email", label: "Email" },
    { key: "group", label: "Group", render: (r) => groupLabel(classById[r.class_id]) },
    { key: "status", label: "Status" },
    { key: "created_at", label: "Date added", render: (r) => formatPT(r.created_at) },
  ];

  const Card = ({ r }) => (
    <div key={r.id} className="paper-card p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid={`reg-${r.id}`}>
      <div className="flex-1 min-w-0">
        <p className="font-serif text-xl text-ink leading-tight">{r.student_name}</p>
        <p className="font-mono text-xs text-stone mt-0.5">via {r.channel} · Date added — {formatPT(r.created_at)}</p>
        <div className="mt-2"><GroupPill cls={classById[r.class_id]} /></div>
      </div>
      <div className="flex items-center gap-4">
        {r.status === "approved" && r.seat_number && (
          <span className="font-mono text-xs text-stone">Cast #{r.seat_number}</span>
        )}
        {tab === "pending" ? (
          <div className="flex gap-2">
            <button onClick={() => act(r.id, "approve")} disabled={busy === r.id} className="btn-brass text-sm px-4 py-2" data-testid={`approve-${r.id}`}>
              <Check size={15} /> Approve
            </button>
            <button onClick={() => act(r.id, "decline")} disabled={busy === r.id} className="btn-outline text-sm px-4 py-2 !border-garnet !text-garnet hover:!bg-garnet/10" data-testid={`decline-${r.id}`}>
              <X size={15} /> Decline
            </button>
          </div>
        ) : (
          <StatusMark status={r.status} />
        )}
      </div>
    </div>
  );

  const groups = [
    ...classes.filter((c) => filtered.some((r) => r.class_id === c.id)).map((c) => ({ cls: c, rows: filtered.filter((r) => r.class_id === c.id) })),
  ];

  return (
    <div className="space-y-8" data-testid="reef-registrations">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="overline mb-2">Who's in the room</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight">Registrations</h1>
          <p className="font-sans text-sm text-stone mt-2">You decide who joins. Nothing auto-approves.</p>
        </div>
        <ExportMenu
          list="registrations"
          params={{ status: tab, group: filter === "all" ? "" : filter, sort }}
          title="Registrations"
          columns={printCols}
          rows={filtered}
        />
      </div>

      <div className="flex gap-2 border-b border-hairline overflow-x-auto no-print">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 font-sans text-sm capitalize border-b-2 -mb-px transition-colors whitespace-nowrap ${
              tab === t ? "border-brass text-ink" : "border-transparent text-stone hover:text-ink"
            }`}
            data-testid={`reg-tab-${t}`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="space-y-3 no-print">
        <div className="chip-row" data-testid="reg-filter-chips">
          <button onClick={() => setFilter("all")} className={`filter-chip px-3 py-1 text-xs font-sans border ${filter === "all" ? "border-brass text-ink bg-brass/10" : "border-hairline text-stone"}`} data-testid="reg-filter-all">
            All ({regs.length})
          </button>
          {classes.map((c) => (
            <button key={c.id} onClick={() => setFilter(c.id)} className={`filter-chip px-3 py-1 text-xs font-sans border ${filter === c.id ? "border-brass text-ink bg-brass/10" : "border-hairline text-stone"}`} data-testid={`reg-filter-${c.id}`}>
              {c.admin_label || c.title} ({regs.filter((r) => r.class_id === c.id).length})
            </button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <select className="field-input !py-1.5 text-sm w-full sm:w-48" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="reg-sort">
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
          <label className="flex items-center gap-2 font-sans text-sm text-ink cursor-pointer">
            <input type="checkbox" checked={groupBy} onChange={(e) => setGroupBy(e.target.checked)} data-testid="reg-group-by-toggle" />
            Group by class
          </label>
        </div>
      </div>

      {groupBy ? (
        <div className="space-y-8">
          {groups.map(({ cls, rows: grows }) => (
            <div key={cls.id} data-testid={`reg-group-${cls.id}`}>
              <div className="mb-3">
                <div className="h-1 w-full mb-2" style={{ background: `var(--group-${cls.color || "blue"})` }} />
                <p className="font-serif text-xl text-ink">
                  {groupLabel(cls)} <span className="font-sans text-xs text-stone">{grows.length} registration{grows.length === 1 ? "" : "s"}</span>
                </p>
              </div>
              <div className="space-y-3">{grows.map((r) => <Card key={r.id} r={r} />)}</div>
            </div>
          ))}
          {groups.length === 0 && <p className="font-sans text-sm text-stone">Nothing here.</p>}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => <Card key={r.id} r={r} />)}
          {filtered.length === 0 && <p className="font-sans text-sm text-stone">Nothing here.</p>}
        </div>
      )}
    </div>
  );
}
